import React, { useState, useEffect, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Settings from "./pages/Settings";
import AddNewPage from "./pages/AddNewPage";
import AllPages from "./pages/AllPages";
import LegalPageTemplates from "./pages/LegalPageTemplates";
import EditTemplatePage from "./pages/LegalPageTemplates/components/Edittemplatepage";
import HelpSupport from "./pages/HelpSupport";
import UpgradeToPro from "./pages/UpgradeToPro";
import ProFeatures from "./pages/ProFeatures";
import DashboardHeader from "../../common/DashboardHeader";
import CreateTemplatePage from "./pages/LegalPageTemplates/components/CreateTemplatePage";
import AllPopups from "./pages/AllPopups";
import AddNewPopup from "./pages/AddNewPopup";
import CookieBar from "./pages/CookieBar";
import License from "./pages/License";

const App = () => {

    const isProActive = LEGAL_PAGES?.is_pro_active;
    const licenseStatus = LEGAL_PAGES?.license?.status;
    const isLicenseValid = licenseStatus === "valid";

// Only allow PRO routes if both are true
const canAccessProRoutes = isProActive && isLicenseValid;
    const [activeTab, setActiveTab] = useState("");
    const [page, setPage] = useState(1);

    const getPageFromPath = (path) => {
        const pageRegex = /\/page\/(\d+)$/;
        const match = path.match(pageRegex);
        return match ? Number(match[1]) : 1;
    };


    useEffect(() => {
        const handleHashChange = () => {
            const protectedRoutes = [
                "/all-popups",
                "/cookie-bar",
                "/add-new-popup",
                "/license",
            ];

            if (!canAccessProRoutes && protectedRoutes.includes(hashPath)) {
                window.location.hash = "/upgrade-to-pro";
                return;
            }

            const rawHash = window.location.hash.replace("#", "") || "";
            if (
                rawHash === "/pro-features/all-popups" ||
                rawHash === "/pro-features/cookie-bar"
            ) {
                window.location.hash = "/upgrade-to-pro";
                return;
            }

            // Check if this is an edit-template route
            const editTemplateMatch = rawHash.match(/^\/edit-template\/\d+/);
            let hashPath, currentPage;

            if (editTemplateMatch) {
                // For edit-template routes, the path is the whole match
                hashPath = '/edit-template';
                currentPage = 1; // Not used for edit-template
            } else {
                // For other routes, split by /page/ to get pagination
                [hashPath] = rawHash.split("/page/");
                currentPage = getPageFromPath(rawHash);
            }

            setActiveTab(hashPath);
            setPage(currentPage);
        };

        window.addEventListener("hashchange", handleHashChange);
        handleHashChange();

        return () => window.removeEventListener("hashchange", handleHashChange);
    }, []);


    const renderContent = () => {
        // Extract template ID from URL for edit-template route
        const editTemplateMatch = window.location.hash.match(/#\/edit-template\/(\d+)/);
        const templateId = editTemplateMatch ? parseInt(editTemplateMatch[1], 10) : null;

        switch (activeTab) {
            case "":
            case "/":
                return <Settings page={page} />;
            case "/add-new":
                return <AddNewPage page={page} />;
            case "/all-legal-pages":
                return <AllPages page={page} />;
            case "/legal-page-templates":
                return <LegalPageTemplates page={page} />;
            case "/edit-template":
                return templateId ? <EditTemplatePage templateId={templateId} /> : <Settings page={page} />;
                case "/add-new-template":
                return <CreateTemplatePage page={page} />;
            case "/help-support":
                return <HelpSupport page={page} />;
            case "/upgrade-to-pro":
                return <UpgradeToPro page={page} />;
            case "/pro-features":
                return <ProFeatures page={page} />;
            case "/pro-features/all-popups":
                return <UpgradeToPro page={page} />;
            case "/pro-features/cookie-bar":
                return <UpgradeToPro page={page} />;

            // ── PRO-only routes ──
            case "/all-popups":
                return canAccessProRoutes
                    ? <AllPopups page={page} />
                    : <UpgradeToPro page={page} />;

            case "/cookie-bar":
                return canAccessProRoutes
                    ? <CookieBar page={page} />
                    : <UpgradeToPro page={page} />;

            case "/add-new-popup":
                return canAccessProRoutes
                    ? <AddNewPopup page={page} />
                    : <UpgradeToPro page={page} />;

            case "/license":
                return <License page={page} />;
            // case "/edit-popup":
            //     return templateId ? <EditPopup templateId={templateId} /> : <Settings page={page} />;

            default:
                return (
                    <div className="p-8">
                        <h2 className="text-2xl font-bold text-red-600">404 - Route not found</h2>
                        <p className="mt-4">Current hash: {window.location.hash}</p>
                        <p>Active tab detected: <strong>{activeTab}</strong></p>
                    </div>
                );
        }
    };

    return (
        <>
            <DashboardHeader
                title="Dashboard"
                viewSiteUrl={LEGAL_PAGES?.site_url}
                notificationCount={1}
            />
            <div className="bg-gray-50">
                <div className="">
                    <div className="w-full mx-auto">
                        {renderContent()}
                    </div>
                </div>

                <ToastContainer position="bottom-right" />
            </div>
        </>
    );
};

// Mount React app
const container = document.getElementById("adl-legal-pages");
if (container) {
    const root = createRoot(container);
    root.render(<App />);
}